/**
 * TenantPicker — searchable tenant link for the work-report form (C3).
 *
 * Third thin wrapper over the shared ``AsyncCombobox`` (C0), alongside
 * ``TaskPicker`` and ``TenancyPicker``. It owns only the tenant
 * vocabulary — how to phrase a person for a human — and delegates
 * debounce, paging, stale-response protection, keyboard and theming to
 * the base.
 *
 * Replaces a native <select> filled with ``fetchAdminTenants({limit:
 * 200})``: past 200 tenants the rest were unreachable, and an edit form
 * could not display a link that fell outside that first page.
 *
 * Rendered shape:
 *   Kamal Omran
 *   kamal@example.test · +41 79 000 00 00
 *
 * Server contract:
 *   GET /api/admin/tenants?q=…&limit&skip   (already existed, unchanged)
 *   GET /api/admin/tenants/{id}             for hydrating the current link
 *
 * The payload it feeds back is unchanged: a bare ``tenant_id`` string.
 */
import React from "react";

import AsyncCombobox from "../../../../components/ui/AsyncCombobox";
import {
  fetchAdminTenant,
  fetchAdminTenantsPage,
} from "../../../../api/adminData";

const PAGE_SIZE = 20;

/**
 * "Kamal Omran" — the API serializes a canonical ``display_name``; the
 * remaining branches only matter for rows written before that field
 * existed. Never a UUID, never ``undefined``.
 * Exported for tests.
 */
export function tenantName(tenant) {
  const display = (tenant?.display_name || tenant?.full_name || "").trim();
  if (display) return display;
  const composed = [tenant?.first_name, tenant?.last_name]
    .map((p) => (p || "").trim())
    .filter(Boolean)
    .join(" ");
  if (composed) return composed;
  const legacy = (tenant?.name || "").trim();
  if (legacy) return legacy;
  const email = (tenant?.email || "").trim();
  if (email) return email;
  const phone = (tenant?.phone || "").trim();
  if (phone) return phone;
  return "Mieter ohne Namen";
}

/**
 * "kamal@example.test · +41 79 000 00 00", or whichever half exists.
 * Returns null when neither does, so no empty separator is rendered.
 * Exported for tests.
 */
export function tenantContact(tenant) {
  return (
    [(tenant?.email || "").trim(), (tenant?.phone || "").trim()]
      .filter(Boolean)
      .join(" · ") || null
  );
}

export default function TenantPicker({ value, onChange, disabled = false }) {
  const [selected, setSelected] = React.useState(null);

  // Hydrate the CURRENT link by id (edit mode, and C1.1 prefill from a
  // task). Without this a linked tenant that sorts past page 1 would
  // render as "nothing selected" and a save could drop the link.
  React.useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected && String(selected.id) === String(value)) return undefined;
    let cancelled = false;
    fetchAdminTenant(value)
      .then((tenant) => {
        if (!cancelled && tenant) setSelected(tenant);
      })
      .catch(() => {
        // A deleted or inaccessible tenant must not break the form; the
        // link stays intact until the user changes it.
      });
    return () => {
      cancelled = true;
    };
  }, [value, selected]);

  // NOTE: ``fetchAdminTenantsPage``, not ``fetchAdminTenants`` — the
  // latter unwraps the response to a bare item array and the base
  // combobox reads ``{items, total}`` (see the C2 regression).
  const fetchPage = React.useCallback(
    ({ query, offset, limit }) =>
      fetchAdminTenantsPage({ q: query || undefined, limit, offset }),
    []
  );

  const handleChange = React.useCallback(
    (id, item) => {
      setSelected(item);
      onChange(id);
    },
    [onChange]
  );

  return (
    <AsyncCombobox
      label="Mieter (optional)"
      placeholder="— keine —"
      searchPlaceholder="Mieter nach Name, E-Mail oder Telefon suchen …"
      loadingText="Mieter werden geladen …"
      emptyText="Keine passenden Mieter gefunden."
      errorText="Mieter konnten nicht geladen werden."
      value={value}
      selectedItem={selected}
      onChange={handleChange}
      fetchPage={fetchPage}
      getItemId={(t) => t.id}
      getItemLabel={(t) => tenantName(t)}
      pageSize={PAGE_SIZE}
      disabled={disabled}
      renderItem={(tenant) => (
        <span className="block min-w-0">
          <span
            title={tenantName(tenant)}
            className="block truncate text-[13px] font-medium text-foreground"
          >
            {tenantName(tenant)}
          </span>
          {tenantContact(tenant) ? (
            <span
              title={tenantContact(tenant)}
              className="block truncate text-[11px] text-muted-foreground"
            >
              {tenantContact(tenant)}
            </span>
          ) : null}
        </span>
      )}
    />
  );
}
